// Service hook (TS-2.5.01, Resource Accessor layer) - the only place in this feature that calls
// roadmapApi. Fetches the live board on mount, whenever the caller's filter selection changes,
// and on any push-channel invalidation (useInvalidation) per the NFR-1..2 second real-time
// requirement - never on a poll timer (decisions.md D-217 / decomposition.md section 8.5:
// clients "connect and receive; they never invoke", and a broadcast's only job is to tell this
// hook to re-enter the ordinary read chain).
import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../../api/ApiError";
import type { RoadmapView } from "../../../api/generated/contracts";
import type { RoadmapFilterParams } from "../../../api/roadmap";
import { roadmapApi } from "../../../api/roadmap";
import { useInvalidation } from "../../../realtime/useInvalidation";
import { applyBrandingTokens } from "../../../theme/applyBrandingTokens";

export interface UseRoadmapResult {
  readonly view: RoadmapView | null;
  readonly loading: boolean;
  readonly error: ApiError | null;
}

/**
 * Fetches GET /api/roadmap for the given filter (RoadmapController.cs / IRoadmapManager). The
 * caller is expected to memoize `filter` (RoadmapPage does, via useMemo over its selection
 * state) so this effect only re-fires when the selection actually changes.
 */
export function useRoadmap(filter: RoadmapFilterParams): UseRoadmapResult {
  const [view, setView] = useState<RoadmapView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    void roadmapApi
      .getLive(filter)
      .then((next) => {
        setView(next);
        setError(null);
        // This is the first point in the app a RoadmapView (and therefore fresh BrandingTokens)
        // arrives - applyBrandingTokens' own header names exactly this call site.
        applyBrandingTokens(next.branding);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The roadmap could not be loaded."));
      })
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(refresh, [refresh]);
  useInvalidation(refresh);

  return { view, loading, error };
}
